import React, { useState, useEffect, useContext } from 'react';
import { UserContext } from '../App';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  AlertTriangle, 
  Bell, 
  BellRing, 
  Package, 
  Volume2, 
  VolumeX,
  RefreshCw,
  X,
  AlertCircle
} from 'lucide-react';

const LowStockAlert = ({ autoCheck = true, threshold = 5 }) => {
  const { user, API } = useContext(UserContext);
  const [lowStockData, setLowStockData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [showAlert, setShowAlert] = useState(false);
  const [lastCheck, setLastCheck] = useState(null);

  useEffect(() => {
    if (autoCheck && user) {
      checkLowStock();
      
      // Vérifier toutes les 5 minutes
      const interval = setInterval(() => {
        checkLowStock();
      }, 5 * 60 * 1000);
      
      return () => clearInterval(interval);
    }
  }, [user, autoCheck, threshold]);

  const checkLowStock = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/stock/low-stock-alert?threshold=${threshold}`);
      const data = response.data;
      
      setLowStockData(data);
      setLastCheck(new Date());
      
      // Si stock faible détecté et son activé
      if (data.has_low_stock && soundEnabled) {
        playAlertSound();
        
        // Notification toast avec urgence
        const criticalItems = data.low_stock_items.filter(item => item.severity === 'critical');
        if (criticalItems.length > 0) {
          toast.error(`🚨 STOCK CRITIQUE: ${criticalItems.length} produit(s) presque épuisé(s)`, {
            duration: 10000
          });
        } else {
          toast.warning(`⚠️ Stock faible: ${data.low_stock_count} produit(s) à réapprovisionner`, {
            duration: 8000
          });
        }
        
        // Ouvrir automatiquement l'alerte si stock critique
        if (criticalItems.length > 0) {
          setShowAlert(true);
        }
      }
      
    } catch (error) {
      console.error('Error checking low stock:', error);
    } finally {
      setLoading(false);
    }
  };

  const playAlertSound = () => {
    try {
      // Créer un son d'alerte avec Web Audio API
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Son d'alerte (3 bips courts)
      let time = audioContext.currentTime;
      
      for (let i = 0; i < 3; i++) {
        oscillator.frequency.setValueAtTime(800, time);
        gainNode.gain.setValueAtTime(0.3, time);
        gainNode.gain.setValueAtTime(0, time + 0.2);
        time += 0.4;
      }
      
      oscillator.start();
      oscillator.stop(time);
      
    } catch (error) {
      console.error('Error playing alert sound:', error);
      // Fallback: utiliser une notification système
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification('🚨 Alerte Stock Faible', {
          body: `${lowStockData?.low_stock_count || 0} produit(s) en stock faible`,
          icon: '/favicon.ico'
        });
      }
    }
  };

  const requestNotificationPermission = async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      await Notification.requestPermission();
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'text-red-600 bg-red-50 border-red-200';
      case 'warning': return 'text-orange-600 bg-orange-50 border-orange-200';
      default: return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical': return <AlertCircle className="h-5 w-5 text-red-600" />;
      case 'warning': return <AlertTriangle className="h-5 w-5 text-orange-600" />;
      default: return <AlertTriangle className="h-5 w-5 text-yellow-600" />;
    }
  };

  // Ne pas afficher si pas de données ou pas d'alerte
  if (!lowStockData || !lowStockData.has_low_stock) {
    return (
      <div className="flex items-center gap-2">
        <Button
          onClick={checkLowStock}
          disabled={loading}
          size="sm"
          variant="outline"
          className="bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
          title="Vérifier les stocks"
        >
          {loading ? (
            <RefreshCw className="h-4 w-4 animate-spin" />
          ) : (
            <Package className="h-4 w-4" />
          )}
          {!lowStockData ? 'Vérifier Stock' : 'Stock OK'}
        </Button>
        
        <button
          onClick={() => setSoundEnabled(!soundEnabled)}
          className={`p-2 rounded ${soundEnabled ? 'text-blue-600' : 'text-gray-400'}`}
          title={soundEnabled ? 'Désactiver les alertes sonores' : 'Activer les alertes sonores'}
        >
          {soundEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      {/* Bouton d'alerte clignotant */}
      <Dialog open={showAlert} onOpenChange={setShowAlert}>
        <DialogTrigger asChild>
          <Button
            className={`relative ${
              lowStockData.low_stock_items.some(item => item.severity === 'critical') 
                ? 'bg-red-600 hover:bg-red-700 animate-pulse' 
                : 'bg-orange-600 hover:bg-orange-700'
            } text-white shadow-lg`}
            data-testid="low-stock-alert-btn"
          >
            <BellRing className="h-4 w-4 mr-2" />
            🚨 Stock Faible ({lowStockData.low_stock_count})
            
            {/* Badge de notification */}
            <div className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center animate-bounce">
              {lowStockData.low_stock_count}
            </div>
          </Button>
        </DialogTrigger>
        
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-6 w-6 text-red-600" />
              🚨 Alerte Stock Faible
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Résumé de l'alerte */}
            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
              <div className="flex items-center gap-3">
                <AlertCircle className="h-6 w-6 text-red-600" />
                <div>
                  <p className="font-semibold text-red-800">
                    {lowStockData.low_stock_count} produit(s) nécessitent un réapprovisionnement
                  </p>
                  <p className="text-sm text-red-700">
                    Seuil d'alerte : {threshold} unités ou moins
                  </p>
                </div>
              </div>
            </div>

            {/* Liste des produits en stock faible */}
            <div className="space-y-3">
              <h4 className="font-medium text-gray-900">Produits à réapprovisionner :</h4>
              
              {lowStockData.low_stock_items.map((item, index) => (
                <div key={index} className={`p-4 rounded-lg border ${getSeverityColor(item.severity)}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {getSeverityIcon(item.severity)}
                      <div>
                        <p className="font-medium">
                          {item.designation}
                        </p>
                        <p className="text-sm">
                          Stock restant: <span className="font-semibold">{item.available_quantity} unités</span>
                        </p>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <Badge variant={item.severity === 'critical' ? "destructive" : "secondary"}>
                        {item.severity === 'critical' ? '🔴 CRITIQUE' : '🟡 FAIBLE'}
                      </Badge>
                      <p className="text-xs mt-1">
                        Seuil: {item.threshold}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Actions recommandées */}
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h4 className="font-medium text-blue-800 mb-2">🎯 Actions Recommandées :</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Vérifiez les fournisseurs disponibles</li>
                <li>• Planifiez les achats dans la section Stock</li>
                <li>• Contactez vos fournisseurs habituels</li>
                <li>• Informez l'équipe de production</li>
              </ul>
            </div>

            {/* Boutons d'action */}
            <div className="flex justify-between items-center pt-4 border-t">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setSoundEnabled(!soundEnabled)}
                  className={`flex items-center gap-2 px-3 py-2 rounded text-sm ${
                    soundEnabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {soundEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
                  Son {soundEnabled ? 'activé' : 'désactivé'}
                </button>
                
                <Button
                  onClick={requestNotificationPermission}
                  size="sm"
                  variant="outline"
                  className="text-xs"
                >
                  <Bell className="h-3 w-3 mr-1" />
                  Activer Notifications
                </Button>
              </div>
              
              <div className="flex gap-2">
                <Button
                  onClick={checkLowStock}
                  disabled={loading}
                  size="sm"
                  variant="outline"
                >
                  <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                  Actualiser
                </Button>
                
                <Button
                  onClick={() => window.open('/stock', '_blank')}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                  size="sm"
                >
                  <Package className="h-4 w-4 mr-1" />
                  Aller au Stock
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Contrôle du son */}
      <button
        onClick={() => setSoundEnabled(!soundEnabled)}
        className={`p-2 rounded ${soundEnabled ? 'text-blue-600' : 'text-gray-400'}`}
        title={soundEnabled ? 'Désactiver les alertes sonores' : 'Activer les alertes sonores'}
      >
        {soundEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
      </button>
      
      {/* Dernière vérification */}
      {lastCheck && (
        <span className="text-xs text-gray-500">
          Vérifié: {lastCheck.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
        </span>
      )}
    </div>
  );
};

export default LowStockAlert;